import { ButtonSpeedDial, FloatingLoader } from "@/components";
import { useDebounce } from "@/hooks/useDebounce";
import useSyncMutation from "@/hooks/useSyncMutation";
import { QUERY_KEYS } from "@/libraries/constant";
import { getMemberChatsOptions } from "@/queries";
import { ChatListSearchSchema } from "@/schemas";
import { useSyncStore } from "@/stores/sync";
import { onlineManager, useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useEffect, useMemo, useRef } from "react";
import AppBarChat from "../-components/AppBar";
import ChatItem from "../-components/ChatItem";
import EmptyChat from "../-components/Empty";

export const Route = createFileRoute("/dashboard/chat/single/")({
	validateSearch: zodSearchValidator(ChatListSearchSchema),
	loader: ({ context }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureInfiniteQueryData(
				getMemberChatsOptions(),
			);
		}
		return context.queryClient.getQueryData([QUERY_KEYS.GET_MEMBER_CHATS]);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const searchQ = Route.useSearch({
		select: (state: ChatListSearchSchema) => state.q ?? "",
	});
	const debouncedSearch = useDebounce(searchQ, 300);
	const scrollRef = useRef<HTMLDivElement>(null);
	const memberSyncChatLastUnix = useSyncStore((s) => s.memberSyncChatLastUnix);
	const { memberSyncChat } = useSyncMutation();

	const memberChatsQuery = useSuspenseInfiniteQuery(getMemberChatsOptions());
	const peoples = useMemo(() => {
		const items = memberChatsQuery.data || [];
		if (!debouncedSearch) return items;
		return items.filter(
			(people) =>
				people?.fullname
					?.toLowerCase()
					?.includes(debouncedSearch.toLowerCase()) ||
				people?.first_name
					?.toLowerCase()
					?.includes(debouncedSearch.toLowerCase()) ||
				people?.last_name
					?.toLowerCase()
					?.includes(debouncedSearch.toLowerCase()) ||
				people?.email?.toLowerCase()?.includes(debouncedSearch.toLowerCase()),
		);
	}, [memberChatsQuery.data, debouncedSearch]);

	const isLoading = useMemo(
		() =>
			memberChatsQuery.isLoading ||
			memberChatsQuery.isFetching ||
			memberChatsQuery.isFetchingNextPage,
		[
			memberChatsQuery.isLoading,
			memberChatsQuery.isFetching,
			memberChatsQuery.isFetchingNextPage,
		],
	);

	const isEmpty = useMemo(
		() => !isLoading && !searchQ && !peoples.length,
		[isLoading, peoples, searchQ],
	);

	const isEmptySearch = useMemo(
		() => !isLoading && !!searchQ && !peoples.length,
		[isLoading, peoples, searchQ],
	);

	const virtualizer = useVirtualizer({
		count: peoples.length,
		overscan: 5,
		gap: 16,
		paddingStart: 16,
		paddingEnd: peoples.length ? 295 : undefined,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => 68,
	});

	const memberVirtualizerItems = virtualizer.getVirtualItems();

	const onClickAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		return navigate({
			to: "/dashboard/chat/single/create",
		});
	};

	const onClickSync = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		return memberSyncChat.mutate({
			last_requested_at: memberSyncChatLastUnix,
		});
	};

	useEffect(() => {
		const [lastItem] = [...memberVirtualizerItems].reverse();

		if (!lastItem) return;

		if (
			lastItem.index >= peoples.length - 1 &&
			memberChatsQuery.hasNextPage &&
			!memberChatsQuery.isFetchingNextPage
		) {
			memberChatsQuery.fetchNextPage();
		}
	}, [
		memberChatsQuery.hasNextPage,
		memberChatsQuery.fetchNextPage,
		peoples.length,
		memberChatsQuery.isFetchingNextPage,
		memberVirtualizerItems,
	]);

	return (
		<>
			<AppBarChat />
			<div ref={scrollRef} className="relative h-full w-full overflow-y-auto">
				<FloatingLoader open={isLoading} />
				{!isEmpty || !isEmptySearch ? (
					<div
						className="flex flex-col gap-3"
						style={{
							height: virtualizer.getTotalSize(),
						}}
					>
						{memberVirtualizerItems.map((groupVirtualizerItem) => {
							const people = peoples[groupVirtualizerItem.index];
							return (
								<Link
									key={groupVirtualizerItem.key}
									to="/dashboard/chat/single/$id/$channelName"
									params={{ id: people.id, channelName: people.channel_name }}
									className="container absolute top-0 left-0"
									style={{
										height: groupVirtualizerItem.size,
										transform: `translateY(${groupVirtualizerItem.start}px)`,
									}}
								>
									<ChatItem
										type="single"
										title={people.fullname}
										unread={people.unread}
										lastMessage={people.last_message}
										avatar={people.avatar}
										status={people.status}
									/>
								</Link>
							);
						})}
					</div>
				) : null}
				{isEmptySearch ? (
					<EmptyChat title={`No results found for '${debouncedSearch}'`} />
				) : null}
				{isEmpty ? (
					<EmptyChat
						title="Connect, learn, and grow in this channel!"
						addLabel="Start Chatting"
						onClickAdd={onClickAdd}
					/>
				) : null}
			</div>
			<ButtonSpeedDial
				mb={0}
				bottom={0}
				onClickAdd={onClickAdd}
				syncProcessing={memberSyncChat.isPending}
				onClickSync={onClickSync}
			/>
		</>
	);
}
